import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import MobilePageGuard from './components/MobilePageGuard';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { PWAProvider } from './context/PWAContext';
import { Toaster } from 'sonner';
import { CopilotProvider } from './copilot/CopilotContext';
import { installRetryInterceptor } from './utils/retryInterceptor';
import ConnectionBanner from './components/ConnectionBanner';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Aszf = lazy(() => import('./pages/Aszf'));
const Privacy = lazy(() => import('./pages/Privacy'));
const PublicPayPage = lazy(() => import('./pages/PublicPayPage'));
const Clients = lazy(() => import('./pages/Clients'));
const Invoices = lazy(() => import('./pages/Invoices'));
const IncomingInvoices = lazy(() => import('./pages/IncomingInvoices'));
const RecurringInvoices = lazy(() => import('./pages/RecurringInvoices'));
const BankStatementHistory = lazy(() => import('./pages/BankStatementHistory'));
const AccountantDashboard = lazy(() => import('./pages/AccountantDashboard'));
const Documents = lazy(() => import('./pages/Documents'));
const DocumentArchive = lazy(() => import('./pages/DocumentArchive'));
const DashboardV2 = lazy(() => import('./pages/DashboardV2'));
const Projects = lazy(() => import('./pages/Projects'));
const WorkersPage = lazy(() => import('./components/workers/WorkersPage'));
const Attendance = lazy(() => import('./pages/Attendance'));
const PriceList = lazy(() => import('./pages/PriceList'));
const ManagerCalendar = lazy(() => import('./pages/ManagerCalendar'));
const DefectReports = lazy(() => import('./pages/DefectReports'));
const Protocols = lazy(() => import('./pages/Protocols'));
const Surveys = lazy(() => import('./pages/Surveys'));
const WorkerPortal = lazy(() => import('./pages/WorkerPortal'));
const GuestPortal = lazy(() => import('./pages/GuestPortal'));
const CustomerPortal = lazy(() => import('./pages/CustomerPortal'));
const WorkerAccount = lazy(() => import('./pages/WorkerAccount'));
const WorkerHub = lazy(() => import('./pages/WorkerHub'));
const Orders = lazy(() => import('./pages/Orders'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const Products = lazy(() => import('./pages/Products'));
const Debts = lazy(() => import('./pages/Debts'));
const Payments = lazy(() => import('./pages/Payments'));
const Reports = lazy(() => import('./pages/Reports'));
const Settings = lazy(() => import('./pages/Settings'));
const CompanySetupWizard = lazy(() => import('./pages/CompanySetupWizard'));
const AIChat = lazy(() => import('./pages/AIChat'));
const Guide = lazy(() => import('./pages/Guide'));
const BillingIntegrations = lazy(() => import('./pages/BillingIntegrations'));
const InvoiceWizard = lazy(() => import('./pages/InvoiceWizard'));
const Automation = lazy(() => import('./pages/Automation'));
const Trash = lazy(() => import('./pages/Trash'));
const QRPayments = lazy(() => import('./pages/QRPayments'));
const Proposals = lazy(() => import('./pages/Proposals'));
const Marketing = lazy(() => import('./pages/Marketing'));
const Predictions = lazy(() => import('./pages/Predictions'));
const DebtRules = lazy(() => import('./pages/DebtRules'));
const ServiceRegistry = lazy(() => import('./pages/ServiceRegistry'));
const BankReconciliation = lazy(() => import('./pages/BankReconciliation'));
const Landing = lazy(() => import('./pages/Landing'));
const Webshop = lazy(() => import('./pages/Webshop'));
const WebshopSuccess = lazy(() => import('./pages/WebshopSuccess'));
const CustomTemplates = lazy(() => import('./pages/CustomTemplates'));
const ClientPortal = lazy(() => import('./pages/ClientPortal'));
const ProposalSign = lazy(() => import('./pages/ProposalSign'));
const DefectReportSign = lazy(() => import('./pages/DefectReportSign'));
const ProjectStatusPublic = lazy(() => import('./pages/ProjectStatusPublic'));
const ProjectRequestPublic = lazy(() => import('./pages/ProjectRequestPublic'));
const ProjectRequests = lazy(() => import('./pages/ProjectRequests'));

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-base">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    // iter 318 — kezdőképernyőre tett app dolgozónál: van dolgozói session, nincs tulaj → a dolgozói hubra
    if (window.location.pathname === '/' && localStorage.getItem('acx_wacc_session')) return <Navigate to="/me" replace />;
    // Mély link (pl. QR: /workers?link=…) → belépés után visszavisszük oda
    const next = window.location.pathname + window.location.search;
    return <Navigate to={next !== '/' ? `/login?next=${encodeURIComponent(next)}` : '/login'} replace />;
  }

  // Accountant role is confined to /accountant dashboard
  if (user.role === 'accountant' && !window.location.pathname.startsWith('/accountant')) {
    return <Navigate to="/accountant" replace />;
  }

  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-base">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (user) {
    // Route user based on role (vagy a bejelentkezés előtti mély linkre)
    const next = new URLSearchParams(window.location.search).get('next');
    if (next && next.startsWith('/') && !next.startsWith('//') && user.role !== 'accountant') return <Navigate to={next} replace />;
    return <Navigate to={user.role === 'accountant' ? '/accountant' : '/'} replace />;
  }

  return children;
}

function AdminHiddenRouteGuard({ children }) {
  // iter 251c — Nem-admin route guard.
  // Az AdminPanel-nek külön (HttpOnly cookie-alapú) rejtett auth-ja van
  // (jelszó: "Helytelen"). Ha egy másik szerepben BEJELENTKEZETT user
  // eljut a /admin-ra (pl. tévedésből egy linkről), inkább hazadobjuk,
  // hogy ne is lássa az admin login képernyőt. Ha nincs JWT session
  // egyáltalán, engedjük tovább — így a rejtett admin login flow megmarad.
  // iter 256 — Az admin panel KIZÁRÓLAG a login oldal alján lévő pajzs ikonról
  // érhető el (kijelentkezett állapotban). Bejelentkezett user-session mellett
  // a /admin mindig hazadob — a cég-szintű 'admin' szerep nem platform-admin.
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Suspense fallback={<div className="min-h-[50vh] flex items-center justify-center" data-testid="route-loading"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800" /></div>}>
    <Routes>
      <Route path="/landing" element={<Landing />} />
      <Route path="/admin" element={
        <AdminHiddenRouteGuard>
          <AdminPanel />
        </AdminHiddenRouteGuard>
      } />
      <Route path="/portal/:token" element={<ClientPortal />} />
      <Route path="/p/:token" element={<ProposalSign />} />
      <Route path="/hj/:token" element={<DefectReportSign />} />
      <Route path="/projekt/:token" element={<ProjectStatusPublic />} />
      <Route path="/igeny/:token" element={<ProjectRequestPublic />} />
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/aszf" element={<Aszf />} />
      <Route path="/pay/:invoiceId" element={<PublicPayPage />} />
      <Route path="/adatvedelem" element={<Privacy />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Aszf />} />
      <Route path="/" element={
        <ProtectedRoute>
          <Layout>
            <DashboardV2 />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/clients" element={
        <ProtectedRoute>
          <Layout>
            <Clients />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/invoices" element={
        <ProtectedRoute>
          <Layout>
            <Invoices />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/invoices/new" element={
        <ProtectedRoute>
          <InvoiceWizard />
        </ProtectedRoute>
      } />
      <Route path="/bank" element={
        <ProtectedRoute>
          <Layout>
            <BankReconciliation />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/bank-statement-history" element={
        <ProtectedRoute>
          <Layout>
            <BankStatementHistory />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/incoming-invoices" element={
        <ProtectedRoute>
          <Layout>
            <IncomingInvoices />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/recurring-invoices" element={
        <ProtectedRoute>
          <Layout>
            <RecurringInvoices />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/accountant" element={
        <ProtectedRoute>
          <AccountantDashboard />
        </ProtectedRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute>
          <Layout>
            <Documents />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/document-archive" element={
        <ProtectedRoute>
          <Layout>
            <DocumentArchive />
          </Layout>
        </ProtectedRoute>
      } />
      {/* iter 279 — egy kanonikus irányítópult: a régi utak átirányítanak */}
      <Route path="/irányitopult" element={<Navigate to="/" replace />} />
      <Route path="/dashboard-v2" element={<Navigate to="/" replace />} />
      <Route path="/dashboard-legacy" element={<Navigate to="/" replace />} />
      <Route path="/projects" element={
        <ProtectedRoute>
          <Layout>
            <Projects />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/w/:token" element={<WorkerPortal />} />
      <Route path="/g/:token" element={<GuestPortal />} />
      <Route path="/c/:token" element={<CustomerPortal />} />
      <Route path="/dolgozo" element={<WorkerAccount />} />
      <Route path="/me" element={<WorkerHub />} />
      <Route path="/workers" element={<ProtectedRoute><Layout><WorkersPage type="employee" /></Layout></ProtectedRoute>} />
      <Route path="/subcontractors" element={<Navigate to="/clients?filter=sub" replace />} />
      <Route path="/attendance" element={<ProtectedRoute><Layout><Attendance /></Layout></ProtectedRoute>} />
      <Route path="/price-list" element={<ProtectedRoute><Layout><PriceList /></Layout></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><Layout><ManagerCalendar /></Layout></ProtectedRoute>} />
      <Route path="/defect-reports" element={<ProtectedRoute><Layout><DefectReports /></Layout></ProtectedRoute>} />
      <Route path="/jegyzokonyvek" element={<ProtectedRoute><Layout><Protocols /></Layout></ProtectedRoute>} />
      <Route path="/igenyek" element={<ProtectedRoute><Layout><ProjectRequests /></Layout></ProtectedRoute>} />
      <Route path="/felmero" element={<ProtectedRoute><Layout><Surveys /></Layout></ProtectedRoute>} />
      <Route path="/projects/:id" element={
        <ProtectedRoute>
          <Layout>
            <ProjectDetail />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/products" element={
        <ProtectedRoute>
          <Layout>
            <MobilePageGuard title="Termékek — asztali nézet"
              hint="A termékek létrehozása és szerkesztése sok mezőt igényel, ami kényelmesebb számítógépen.">
              <Products />
            </MobilePageGuard>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/debts" element={
        <ProtectedRoute>
          <Layout>
            <Debts />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/payments" element={
        <ProtectedRoute>
          <Layout>
            <Payments />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute>
          <Layout>
            <Reports />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/setup-wizard" element={
        <ProtectedRoute>
          <Layout>
            <CompanySetupWizard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Layout>
            <Settings />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/trash" element={
        <ProtectedRoute>
          <Layout>
            <Trash />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/ai-chat" element={
        <ProtectedRoute>
          <Layout>
            <AIChat />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/guide" element={
        <ProtectedRoute>
          <Layout>
            <Guide />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/billing" element={
        <ProtectedRoute>
          <Layout>
            <MobilePageGuard title="Számlázó integrációk — asztali nézet"
              hint="API kulcsok és integráció-beállítások kényelmesebbek számítógépen.">
              <BillingIntegrations />
            </MobilePageGuard>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/automation" element={
        <ProtectedRoute>
          <Layout>
            <MobilePageGuard title="Automatizálás — asztali nézet"
              hint="Szabályok készítéséhez számítógépet javaslunk — ott minden részletet átláthatóan látsz.">
              <Automation />
            </MobilePageGuard>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/qr-payments" element={
        <ProtectedRoute>
          <Layout>
            <QRPayments />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/proposals" element={
        <ProtectedRoute>
          <Layout>
            <Proposals />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/orders" element={
        <ProtectedRoute>
          <Layout>
            <Orders />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/marketing" element={
        <ProtectedRoute>
          <Layout>
            <MobilePageGuard title="Marketing — asztali nézet"
              hint="Kampányok készítéséhez számítógépet javaslunk.">
              <Marketing />
            </MobilePageGuard>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/predictions" element={
        <ProtectedRoute>
          <Layout>
            <Predictions />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/debt-rules" element={
        <ProtectedRoute>
          <Layout>
            <MobilePageGuard title="Behajtási szabályok — asztali nézet"
              hint="Szabályok szerkesztéséhez számítógép kényelmesebb.">
              <DebtRules />
            </MobilePageGuard>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/headquarters" element={<Navigate to="/registry?type=headquarters" replace />} />
      <Route path="/csomagok" element={
        <ProtectedRoute>
          <Layout>
            <Webshop />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/webshop/success" element={
        <ProtectedRoute>
          <WebshopSuccess />
        </ProtectedRoute>
      } />
      <Route path="/sablonok" element={
        <ProtectedRoute>
          <Layout>
            <CustomTemplates />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/registry" element={
        <ProtectedRoute>
          <Layout>
            <MobilePageGuard title="Nyilvántartó — asztali nézet"
              hint="A táblázatos felület kényelmesebb számítógépen.">
              <ServiceRegistry />
            </MobilePageGuard>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

installRetryInterceptor();

function App() {
  // iter 172 — listen for admin-broadcast hard-reload signal (every 60s).
  // The admin can press "Globális reload-broadcast" from the Maintenance tile
  // to push a new reload_version, and every open tab will Ctrl+Shift+R itself.
  // iter 283 — a reload-version ellenőrzés a LiveProvider egyesített pollján keresztül fut (context/LiveContext.js)

  return (
    <BrowserRouter>
      <ErrorBoundary full>
      <ThemeProvider>
        <AuthProvider>
          <PWAProvider>
            <CopilotProvider>
              <AppRoutes />
            </CopilotProvider>
            <ConnectionBanner />
            <Toaster position="top-right" richColors />
          </PWAProvider>
        </AuthProvider>
      </ThemeProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
